import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Image, Video, Star, 
  X, Save, Loader2, ExternalLink, GripVertical, AlertCircle, CheckCircle 
} from 'lucide-react';
import './AdminPortfolio.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPortfolio = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [toast, setToast] = useState({ show: false, type: '', message: '' });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    client_name: '',
    thumbnail_url: '',
    video_url: '',
    is_featured: false,
    display_order: 1
  });

  const categories = [
    'Wedding Film',
    'Wedding Highlight',
    'Wedding Teaser',
    'Corporate',
    'Documentary',
    'Short Film',
    'Social Media',
    'AD Film',
    'Event',
    'Education'
  ];

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/portfolio`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPortfolio(data);
      }
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setSubmitError('');
    setFormData({
      title: '',
      description: '',
      category: '',
      client_name: '',
      thumbnail_url: '',
      video_url: '',
      is_featured: false,
      display_order: portfolio.length + 1
    });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setSubmitError('');
    setFormData({
      title: item.title,
      description: item.description,
      category: item.category,
      client_name: item.client_name || '',
      thumbnail_url: item.thumbnail_url || '',
      video_url: item.video_url || '',
      is_featured: item.is_featured || false,
      display_order: item.display_order || 1
    });
    setShowModal(true);
  };

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: '', message: '' }), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSubmitError('');

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setSubmitError('Session expired. Please login again.');
        setSaving(false);
        return;
      }

      const url = editingItem 
        ? `${API_URL}/api/admin/portfolio/${editingItem.id}`
        : `${API_URL}/api/admin/portfolio`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchPortfolio();
        setShowModal(false);
        showToast('success', editingItem ? 'Portfolio item updated successfully!' : 'Portfolio item added successfully!');
      } else {
        let errorMsg = `Failed to save (HTTP ${response.status})`;
        try {
          const data = await response.json();
          if (data.detail) {
            errorMsg = typeof data.detail === 'string' 
              ? data.detail 
              : (Array.isArray(data.detail) ? data.detail.map(d => d.msg || JSON.stringify(d)).join(', ') : JSON.stringify(data.detail));
          }
        } catch (e) {
          // ignore JSON parse errors
        }
        if (response.status === 401) {
          errorMsg = 'Session expired. Please login again.';
        }
        setSubmitError(errorMsg);
      }
    } catch (error) {
      console.error('Failed to save portfolio item:', error);
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this portfolio item?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/portfolio/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchPortfolio();
      }
    } catch (error) {
      console.error('Failed to delete portfolio item:', error);
    }
  };

  const toggleFeatured = async (item) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/portfolio/${item.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_featured: !item.is_featured })
      });
      fetchPortfolio();
    } catch (error) {
      console.error('Failed to toggle featured:', error);
    }
  };

  return (
    <div className="admin-portfolio" data-testid="admin-portfolio">
      {toast.show && (
        <div className={`portfolio-toast ${toast.type}`} data-testid="portfolio-toast">
          {toast.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          <span>{toast.message}</span>
        </div>
      )}
      <div className="page-header">
        <div>
          <h1>Portfolio</h1>
          <p>Showcase your best work</p>
        </div>
        <button className="add-btn" onClick={openCreateModal} data-testid="add-portfolio-btn">
          <Plus size={18} />
          <span>Add Item</span>
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <Loader2 className="spin" size={32} />
          <span>Loading portfolio...</span>
        </div>
      ) : portfolio.length === 0 ? (
        <div className="empty-container">
          <Image size={48} />
          <h3>No portfolio items yet</h3>
          <p>Add your best work to showcase to clients.</p>
          <button className="add-btn" onClick={openCreateModal}>
            <Plus size={18} />
            <span>Add First Item</span>
          </button>
        </div>
      ) : (
        <div className="portfolio-grid">
          {portfolio.map((item) => (
            <div 
              key={item.id} 
              className={`portfolio-card ${item.is_featured ? 'featured' : ''}`}
              data-testid={`portfolio-card-${item.id}`}
            >
              <div className="card-thumbnail">
                {item.thumbnail_url ? (
                  <img src={item.thumbnail_url} alt={item.title} />
                ) : (
                  <div className="no-thumbnail">
                    <Image size={32} />
                  </div>
                )}
                <div className="card-overlay">
                  <button 
                    className="overlay-btn" 
                    onClick={() => openEditModal(item)}
                    data-testid={`edit-portfolio-${item.id}`}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button 
                    className="overlay-btn delete" 
                    onClick={() => deleteItem(item.id)}
                    data-testid={`delete-portfolio-${item.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                  {item.video_url && item.video_url !== '#' && (
                    <a 
                      href={item.video_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="overlay-btn"
                    >
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>
                {item.is_featured && (
                  <span className="featured-badge">
                    <Star size={12} /> Featured
                  </span>
                )}
              </div>

              <div className="card-content">
                <span className="card-category">{item.category}</span>
                <h3 className="card-title">{item.title}</h3>
                <p className="card-description">{item.description}</p>
                {item.client_name && (
                  <span className="card-client">Client: {item.client_name}</span>
                )}
                <button 
                  className={`featured-toggle ${item.is_featured ? 'active' : ''}`}
                  onClick={() => toggleFeatured(item)}
                >
                  <Star size={14} />
                  {item.is_featured ? 'Remove Featured' : 'Mark as Featured'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Portfolio Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="portfolio-modal">
            <div className="modal-header">
              <h2>{editingItem ? 'Edit Portfolio Item' : 'Add Portfolio Item'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="portfolio-form">
              {submitError && (
                <div className="form-error-banner" data-testid="portfolio-form-error">
                  <AlertCircle size={18} />
                  <span>{submitError}</span>
                </div>
              )}
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Royal Wedding Film"
                  required
                  data-testid="portfolio-title-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    data-testid="portfolio-category-select"
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Client Name</label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="e.g., Private Client"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the project..."
                  rows={3}
                  required
                  data-testid="portfolio-description-input"
                />
              </div>

              <div className="form-group">
                <label>Thumbnail URL *</label>
                <input
                  type="url"
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  required
                />
                {formData.thumbnail_url && (
                  <div className="thumbnail-preview">
                    <img src={formData.thumbnail_url} alt="Preview" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Video URL (YouTube/Vimeo)</label>
                <input
                  type="url"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group checkbox-container">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    />
                    <span>Featured item</span>
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel" 
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-save" 
                  disabled={saving}
                  data-testid="save-portfolio-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      <span>{editingItem ? 'Update' : 'Create'} Item</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPortfolio;
